import { get } from 'dot-prop';
// eslint-disable-next-line sonarjs/slow-regex
const PARAM_REGEXP = /((?:\(\w+[^()]+)?\$[\w.]*\w(?:[^()]*\))?)/g;
const FUNCTION_REGEXP = /\((\w+)\s(.+)\)/;
function isObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
}
function flatParams(source, target, path) {
    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            const nextPath = path ? `${path}.${key}` : `$${key}`;
            const value = source[key];
            if (Array.isArray(value) || isObject(value)) {
                flatParams(value, target, nextPath);
            }
            else {
                target[nextPath] = value;
            }
        }
    }
}
// eslint-disable-next-line sonarjs/function-return-type
export function compile(translation, helpers) {
    const nestedTranslation = Array.isArray(translation) || isObject(translation);
    if (nestedTranslation) {
        const compiled = {};
        for (const key in translation) {
            compiled[key] = compile(translation[key], helpers);
        }
        return compiled;
    }
    const compilationIsNotNeeded = typeof translation !== 'string' || translation.match(PARAM_REGEXP) === null;
    if (compilationIsNotNeeded) {
        return () => translation;
    }
    const instructions = translation.split(PARAM_REGEXP).map((decl) => {
        const paramIndex = decl.search(/\$\w+/);
        if (paramIndex === -1) {
            return () => decl;
        }
        if (paramIndex === 0) {
            return (params) => params[decl];
        }
        // eslint-disable-next-line sonarjs/prefer-regexp-exec
        const options = decl.match(FUNCTION_REGEXP);
        if (!options) {
            return () => decl;
        }
        const fn = helpers[options[1]];
        const args = options[2].split(/\|/g);
        return (params) => fn(...args.map((a) => params[a] || a));
    });
    return (args) => {
        const params = {};
        if (args.length === 1 && (Array.isArray(args[0]) || isObject(args[0]))) {
            flatParams(args[0], params);
        }
        else if (args.length > 0) {
            for (let i = 0; i < args.length; i++) {
                params[`$${i + 1}`] = args[i];
            }
        }
        return instructions.reduce((acc, decl) => acc + decl(params), '');
    };
}
/**
 * Translate factory
 *
 * ```ts
 * import {createTranslate} from '@rambler-tech/translate'
 * import {pluralize} from '@rambler-tech/pluralize'
 *
 * const translation = {
 *   hello: 'Hello',
 *   echo: 'You say: $1',
 *   more: {
 *     age: 'I\'m a (pluralize $1|year|years) old'
 *   },
 *   greeting: 'I\m $name and I\'m (pluralize $age|year|years) old'
 * }
 *
 * const translate = createTranslate(translation, {pluralize})
 *
 * translate('hello') // Hello
 * translate('echo', 'Foo') // You say: Foo
 * translate('more.age', 25) // I'm a 25 years old
 * translate('greeting', {name: 'John Smith', age: 25}) // I'm John Smith and I'm 25 years old
 * ```
 */
export function createTranslate(translation, helpers = {}) {
    const cache = {};
    const compiled = compile(translation, helpers);
    return (key, arg, ...args) => {
        if (cache[key] != null && !arg && args.length === 0) {
            return cache[key];
        }
        if (cache[key] == null) {
            cache[key] = get(compiled, key);
        }
        const translate = cache[key];
        if (typeof translate !== 'function') {
            return key;
        }
        const translated = translate([arg, ...args].filter(Boolean));
        if (!arg && args.length === 0) {
            cache[key] = translated;
        }
        return translated;
    };
}
